/**
 * Corporate Price Lists List Page Component
 * /admin/corporate-price-lists
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import apiClient from '../api/client';
import { exportToCSV } from '../utils/export';

const CorporatePriceLists = () => {
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [customers, setCustomers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadPriceLists();
    loadCustomers();
  }, [customerFilter, activeFilter]);

  const loadCustomers = async () => {
    try {
      const response = await apiClient.getCustomers({ is_active: true });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setCustomers(data);
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  const loadPriceLists = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (customerFilter) params.customer = customerFilter;
      if (activeFilter !== '') params.is_active = activeFilter === 'true';
      if (searchTerm) params.search = searchTerm;
      
      const response = await apiClient.getCorporatePriceLists(params);
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setPriceLists(data);
    } catch (err) {
      setError(err.message || 'Failed to load price lists');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadPriceLists();
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete price list "${name}"? This will also delete all associated route prices.`)) {
      return;
    }
    try {
      await apiClient.deleteCorporatePriceList(id);
      setError('');
      loadPriceLists();
    } catch (err) {
      setError(err.message || 'Failed to delete price list');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading && priceLists.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Corporate Price Lists
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => {
              const headers = [
                { key: 'name', label: 'Name' },
                { key: 'customer_name', label: 'Customer' },
                { key: 'effective_date', label: 'Effective Date' },
                { key: 'expiry_date', label: 'Expiry Date' },
                { key: 'is_active', label: 'Active' },
              ];
              exportToCSV(priceLists, 'corporate-price-lists', headers);
            }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/corporate-price-lists/new')}
          >
            New Price List
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <form onSubmit={handleSearchSubmit} style={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              placeholder="Search price lists by name..."
              value={searchTerm}
              onChange={handleSearch}
              slotProps={{ input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              } }}
            />
          </form>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customerFilter}
              label="Customer"
              onChange={(e) => setCustomerFilter(e.target.value)}
            >
              <MenuItem value="">All Customers</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.customer_name || customer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              label="Status"
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Effective Date</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {priceLists.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No price lists found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              priceLists.map((priceList) => (
                <TableRow key={priceList.id} hover>
                  <TableCell>{priceList.name}</TableCell>
                  <TableCell>
                    {priceList.customer_name || priceList.customer_code || '-'}
                  </TableCell>
                  <TableCell>{formatDate(priceList.effective_date)}</TableCell>
                  <TableCell>{formatDate(priceList.expiry_date)}</TableCell>
                  <TableCell>
                    <Chip
                      label={priceList.is_active ? 'Active' : 'Inactive'}
                      color={priceList.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/corporate-price-lists/${priceList.id}`)}
                      title="View"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/corporate-price-lists/${priceList.id}/edit`)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(priceList.id, priceList.name)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default CorporatePriceLists;

